import type { CSSProp, FlattenSimpleInterpolation } from 'styled-components';
import { css } from 'styled-components';

import { theme } from '@/theme';
import type { Colors } from '@/theme/types';

import type {
  InputColorSet,
  InputColorVariant,
  InputState,
  InputVariant,
} from './DeprecatedInput.types';

export const getColorVariants = (colors: Colors): InputColorSet => ({
  default: {
    color: colors.default,
    borderColor: colors.default,
    backgroundColor: colors.white,
  },
  defaultInverted: {
    color: colors.white,
    borderColor: colors.white,
    backgroundColor: colors.whiteAlpha12,
  },
  opaque: {
    color: colors.default,
    borderColor: colors.default,
    backgroundColor: colors.whiteAlpha32,
  },
  grey: {
    color: colors.default,
    borderColor: colors.default,
    backgroundColor: colors.lightGrey,
  },
  lightGrey: {
    color: colors.default,
    borderColor: colors.defaultAdd5,
    backgroundColor: 'transparent',
  },
});

export const switchColors = (
  colors: Colors,
  colorVariant: InputColorVariant,
  variant: InputVariant,
  state?: InputState,
  disabled?: boolean,
): CSSProp => {
  const variantsColorVariant = getColorVariants(colors);

  const initialStyles = css`
    color: ${variantsColorVariant[colorVariant].color};
    border-color: ${variantsColorVariant[colorVariant].borderColor};
    border-style: solid;
    background-color: ${variantsColorVariant[colorVariant].backgroundColor};

    input {
      color: ${variantsColorVariant[colorVariant].color};
    }

    svg {
      color: ${variantsColorVariant[colorVariant].borderColor};
    }
  `;

  const lightGreyVariantStyles = css`
    svg {
      color: ${colors.secondaryGrey};
    }
  `;

  const boxedBoldVariantStyles = css`
    border-width: ${theme.components.input.variant.boxedBold.borderWidth}px;
  `;

  const boxedVariantStyles = css`
    border-width: ${theme.components.input.variant.boxed.borderWidth}px;
  `;

  const underlinedVariantStyles = css`
    border-bottom-width: 1px;
  `;

  const uncoveredVariantStyles = css`
    border: none;
  `;

  const disabledStyles = css`
    border-color: ${variantsColorVariant[colorVariant].borderColor};
    background-color: ${variantsColorVariant[colorVariant].backgroundColor};
    opacity: 0.6;

    input {
      color: ${variantsColorVariant[colorVariant].color};
    }

    &:hover {
      border-color: ${variantsColorVariant[colorVariant].borderColor};
    }

    svg {
      color: ${variantsColorVariant[colorVariant].borderColor};
    }

    &:hover svg {
      color: ${variantsColorVariant[colorVariant].borderColor};
    }
  `;

  const errorStyles = css`
    border-color: ${colors.error};

    svg {
      color: ${colors.error};
    }

    &:hover {
      border-color: ${colors.error};
    }

    &:hover svg {
      color: ${colors.error};
    }
  `;

  const warningStyles = css`
    border-color: ${colors.warning};

    svg {
      color: ${colors.warning};
    }

    &:hover {
      border-color: ${colors.warning};
    }

    &:hover svg {
      color: ${colors.warning};
    }
  `;

  const successStyles = css`
    border-color: ${colors.success};

    svg {
      color: ${colors.success};
    }

    &:hover {
      border-color: ${colors.success};
    }

    &:hover svg {
      color: ${colors.success};
    }
  `;

  const stateStylesMap: { [K in InputState]?: FlattenSimpleInterpolation } = {
    error: errorStyles,
    success: successStyles,
    warning: warningStyles,
  };

  const getStateStyles = () => {
    if (disabled) {
      return disabledStyles;
    }
    if (state) {
      return stateStylesMap[state];
    }
  };

  const getVariantStyles = (variant: InputVariant) => {
    if (variant === 'boxed') {
      return boxedVariantStyles;
    }
    if (variant === 'boxedBold') {
      return boxedBoldVariantStyles;
    }
    if (variant === 'underlined') {
      return underlinedVariantStyles;
    }
    if (variant === 'uncovered') {
      return uncoveredVariantStyles;
    }
  };

  const getCustomColorVariantStyles = (colorVariant: InputColorVariant) => {
    if (colorVariant === 'lightGrey') {
      return lightGreyVariantStyles;
    }
  };

  return css`
    ${initialStyles};
    ${getVariantStyles(variant)};
    ${getCustomColorVariantStyles(colorVariant)}
    ${getStateStyles}
  `;
};
